import React from "react";
import { JSONSchemaForm } from "@/components/JSONSchemaForm";
import { JSONObject, JSONValue } from "@/components/jsonSchemaForm/types";
import { JSONSchema7 } from "json-schema";
import { ScrollArea } from "@/components/ui/scroll-area";
import { fetchApi } from "@/lib/api";
import { useWorkflowRunStore } from "@/store/workflowRunStore";
import { useStandardAgentStore } from "@/store/standardAgentStore";
import { z } from "zod";
import { AssetSchema } from "@forgepoint/shared";
import { AssetFileViewer } from "@/modules/technicalDesigns/AssetFileViewer";
import { useAutoSave } from "@/modules/quotesDeprecated/QuotePreview/hooks/useAutoSave";
import { cn } from "@/lib/utils";
import {
  Loader2,
  Check,
  AlertCircle,
  X,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { ajv } from "@forgepoint/shared";
import type { ValidateFunction } from "ajv";
import { IndexSelector } from "./partsReview/IndexSelector";
import { Button } from "@/components/ui/button";
import type { RichActionData } from "./helpers/richActionHelpers";

const GetAssetsByIdsResponseSchema = z.object({
  assets: z.array(AssetSchema),
});

const GetUserReviewOutputResponseSchema = z.object({
  output: z.object({
    formJsonSchema: z.string(),
    formJson: z.string(),
    confirmedIndices: z.array(z.number()).optional(),
    source: z.enum(["input", "output"]),
  }),
});

const UserReviewParamsSchema = z.object({
  formJsonSchema: z.string(),
  formJson: z.string(),
  confirmedIndices: z.array(z.number()).optional(),
});

interface UserReviewParams {
  formJsonSchema: JSONSchema7;
  formJson: JSONValue;
  confirmedIndices: number[];
}

const isObjectRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const parseUserReviewParams = (input: unknown): UserReviewParams | null => {
  const parsed = UserReviewParamsSchema.safeParse(input);
  if (!parsed.success) {
    return null;
  }
  try {
    const formJsonSchema = JSON.parse(
      parsed.data.formJsonSchema,
    ) as JSONSchema7;
    const formJson = JSON.parse(parsed.data.formJson) as JSONValue;
    return {
      formJsonSchema,
      formJson,
      confirmedIndices: parsed.data.confirmedIndices ?? [],
    };
  } catch {
    return null;
  }
};

export interface UserReviewFormProps {
  richActionData: RichActionData;
  isApproving: boolean;
  isDenying: boolean;
  onApprove?: (taskId: string) => void;
  onDeny?: (taskId: string) => void;
  onConfirmationChange?: (confirmedParts: Set<number>) => void;
  externalSelectedIndex?: number;
  onSelectedIndexChange?: (index: number) => void;
  sdkThreadId?: string;
  sdkMessageId?: string;
}

export const UserReviewForm: React.FC<UserReviewFormProps> = ({
  richActionData,
  isApproving,
  isDenying,
  onConfirmationChange,
  externalSelectedIndex,
  onSelectedIndexChange,
  sdkThreadId,
  sdkMessageId,
}) => {
  const operationId = richActionData.operationId;
  const hasSdkContext = Boolean(sdkThreadId && sdkMessageId);
  const runMap = useWorkflowRunStore((state) => state.runMap);
  const getWorkflowRun = useWorkflowRunStore((state) => state.getWorkflowRun);
  const saveUserReviewOutput = useStandardAgentStore(
    (state) => state.saveUserReviewOutput,
  );
  const sdkIsSaving = useStandardAgentStore((state) =>
    sdkMessageId
      ? (state.isSavingUserReviewOutput.get(sdkMessageId) ?? false)
      : false,
  );
  const sdkSaveError = useStandardAgentStore((state) =>
    sdkMessageId
      ? (state.userReviewOutputSaveError.get(sdkMessageId) ?? null)
      : null,
  );

  const currentOperationInput = React.useMemo(() => {
    if (!operationId) {
      return null;
    }

    for (const [, run] of runMap) {
      if (!run?.steps) {
        continue;
      }
      for (const step of run.steps) {
        const matchedOperation = step.operations?.find(
          (candidate) => candidate.id === operationId,
        );
        if (matchedOperation) {
          return matchedOperation;
        }
      }
    }
    return null;
  }, [operationId, runMap]);

  const currentOperationUpdatedAt = currentOperationInput?.updatedAt;
  const currentOperationStatus = currentOperationInput?.status;
  const [sdkResolvedParams, setSdkResolvedParams] =
    React.useState<UserReviewParams | null>(null);
  const [sdkResolveError, setSdkResolveError] = React.useState<string | null>(
    null,
  );
  const [isLoadingSdkResolvedParams, setIsLoadingSdkResolvedParams] =
    React.useState(false);

  React.useEffect(() => {
    if (!operationId || !currentOperationInput) {
      return;
    }
    const taskRunId =
      currentOperationInput.task?.agentWorkflowRunId ?? undefined;
    if (taskRunId) {
      getWorkflowRun(taskRunId).catch(() => {});
    }
  }, [getWorkflowRun, operationId, currentOperationInput]);

  React.useEffect(() => {
    if (!hasSdkContext || !sdkMessageId) {
      setSdkResolvedParams(null);
      setSdkResolveError(null);
      setIsLoadingSdkResolvedParams(false);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setIsLoadingSdkResolvedParams(true);
      const response = await fetchApi(
        `standard-agent/messages/${sdkMessageId}/user-review-output`,
        "GET",
      );

      if (cancelled) {
        return;
      }

      const parsedResponse = GetUserReviewOutputResponseSchema.safeParse(
        response.ok ? response.data : null,
      );

      if (!parsedResponse.success) {
        setSdkResolveError("Failed to load user review data");
        setSdkResolvedParams(null);
        setIsLoadingSdkResolvedParams(false);
        return;
      }

      const params = parseUserReviewParams(parsedResponse.data.output);
      if (!params) {
        setSdkResolveError("Failed to load user review data");
        setSdkResolvedParams(null);
        setIsLoadingSdkResolvedParams(false);
        return;
      }

      setSdkResolveError(null);
      setSdkResolvedParams({
        ...params,
        confirmedIndices: parsedResponse.data.output.confirmedIndices ?? [],
      });
      setIsLoadingSdkResolvedParams(false);
    };

    load().catch(() => {
      if (!cancelled) {
        setSdkResolveError("Failed to load user review data");
        setSdkResolvedParams(null);
        setIsLoadingSdkResolvedParams(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [hasSdkContext, sdkMessageId]);

  const userReviewParams = React.useMemo(() => {
    if (hasSdkContext && sdkResolvedParams) {
      return sdkResolvedParams;
    }

    if (operationId && currentOperationInput) {
      const params = currentOperationInput.operationParameters;
      if (
        params.type !== "TOOL_CALL" ||
        params.actionId !== "user_review_action"
      ) {
        return null;
      }
      return parseUserReviewParams(params.input);
    }

    return parseUserReviewParams(richActionData.actionArguments);
  }, [
    hasSdkContext,
    sdkResolvedParams,
    operationId,
    currentOperationInput,
    richActionData.actionArguments,
  ]);

  const formSourceKey = React.useMemo(() => {
    if (operationId) {
      return `op:${operationId}`;
    }
    if (sdkMessageId) {
      return `sdk:${sdkMessageId}`;
    }
    if (richActionData.task?.id) {
      return `task:${richActionData.task.id}`;
    }
    return `args:${JSON.stringify(richActionData.actionArguments)}`;
  }, [
    operationId,
    sdkMessageId,
    richActionData.task?.id,
    richActionData.actionArguments,
  ]);

  const [selectedIndex, setSelectedIndex] = React.useState<number>(0);
  const [userReviewForm, setUserReviewForm] = React.useState<JSONValue | null>(
    userReviewParams?.formJson ?? null,
  );
  const [userReviewSchema, setUserReviewSchema] =
    React.useState<JSONSchema7 | null>(
      userReviewParams?.formJsonSchema ?? null,
    );
  const [selectedAssetId, setSelectedAssetId] = React.useState<string | null>(
    null,
  );
  const [selectedAsset, setSelectedAsset] = React.useState<z.infer<
    typeof AssetSchema
  > | null>(null);
  const [assetLoading, setAssetLoading] = React.useState(false);
  const [assetError, setAssetError] = React.useState<string | null>(null);
  const [validationError, setValidationError] = React.useState<string | null>(
    null,
  );
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [lastSavedAt, setLastSavedAt] = React.useState<Date | null>(null);
  const [formValidator, setFormValidator] =
    React.useState<ValidateFunction<JSONValue> | null>(null);
  const [schemaError, setSchemaError] = React.useState<string | null>(null);
  const [confirmedParts, setConfirmedParts] = React.useState<Set<number>>(
    new Set(),
  );
  const hydratedSourceRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (hydratedSourceRef.current === formSourceKey) {
      return;
    }

    setUserReviewForm(null);
    setUserReviewSchema(null);
    setValidationError(null);
    setSaveError(null);
    setSchemaError(null);
    setLastSavedAt(null);
    setSelectedIndex(0);
    setConfirmedParts(new Set());
    hydratedSourceRef.current = null;
  }, [formSourceKey]);

  React.useEffect(() => {
    if (!userReviewParams) {
      return;
    }
    if (hydratedSourceRef.current === formSourceKey) {
      return;
    }

    setUserReviewForm(userReviewParams?.formJson ?? null);
    setUserReviewSchema(userReviewParams?.formJsonSchema ?? null);
    setValidationError(null);
    setSaveError(null);
    setSchemaError(null);
    setLastSavedAt(null);
    setSelectedIndex(0);

    const loadedIndices = userReviewParams?.confirmedIndices ?? [];
    const validIndices = loadedIndices.filter(
      (idx) =>
        idx >= 0 &&
        (!Array.isArray(userReviewParams?.formJson) ||
          idx < userReviewParams.formJson.length),
    );
    setConfirmedParts(new Set(validIndices));
    hydratedSourceRef.current = formSourceKey;
  }, [formSourceKey, userReviewParams]);

  React.useEffect(() => {
    if (!userReviewSchema) {
      setFormValidator(null);
      setSchemaError(null);
      return;
    }

    try {
      const validator = ajv.compile<JSONValue>(userReviewSchema);
      setFormValidator(() => validator);
      setSchemaError(null);
    } catch {
      setFormValidator(null);
      setSchemaError("Invalid form schema");
    }
  }, [userReviewSchema]);

  const handleSave = React.useCallback(
    async (data: JSONValue) => {
      if (!userReviewSchema) {
        return;
      }

      if (operationId) {
        const baseArgs = richActionData.actionArguments;
        const payload: Record<string, unknown> = {
          ...baseArgs,
          formJson: JSON.stringify(data),
          formJsonSchema: JSON.stringify(userReviewSchema),
          confirmedIndices: Array.from(confirmedParts),
        };

        const response = await fetchApi(
          `agents/operations/${operationId}/input`,
          "PATCH",
          { body: { input: payload } },
        );

        if (!response.ok) {
          const responseData =
            typeof response.data === "object" && response.data !== null
              ? response.data
              : null;
          const errorMessage =
            responseData &&
            "error" in responseData &&
            typeof responseData.error === "string"
              ? responseData.error
              : "Failed to save form";
          setSaveError(errorMessage);
          return;
        }

        setSaveError(null);
        setLastSavedAt(new Date());
        return;
      }

      if (!hasSdkContext || !sdkThreadId || !sdkMessageId) {
        return;
      }

      const success = await saveUserReviewOutput(
        sdkThreadId,
        sdkMessageId,
        data,
        Array.from(confirmedParts),
      );
      if (success) {
        setSaveError(null);
        setLastSavedAt(new Date());
      }
    },
    [
      operationId,
      hasSdkContext,
      sdkThreadId,
      sdkMessageId,
      saveUserReviewOutput,
      richActionData.actionArguments,
      userReviewSchema,
      confirmedParts,
    ],
  );

  const isArrayForm = React.useMemo(() => {
    return Array.isArray(userReviewForm);
  }, [userReviewForm]);

  const formArray = React.useMemo(() => {
    if (!isArrayForm || !Array.isArray(userReviewForm)) {
      return [];
    }
    return userReviewForm.filter(
      (item): item is JSONObject =>
        typeof item === "object" && item !== null && !Array.isArray(item),
    );
  }, [isArrayForm, userReviewForm]);

  const itemSchema = React.useMemo(() => {
    if (!isArrayForm || !userReviewSchema) {
      return userReviewSchema;
    }
    if (isObjectRecord(userReviewSchema.items)) {
      return userReviewSchema.items;
    }
    return userReviewSchema;
  }, [isArrayForm, userReviewSchema]);

  const selectedItem = React.useMemo(() => {
    if (!isArrayForm) {
      return userReviewForm;
    }
    const validIndex = Math.min(selectedIndex, formArray.length - 1);
    return formArray[validIndex] ?? null;
  }, [isArrayForm, selectedIndex, formArray, userReviewForm]);

  const selectedItemAssetIds = React.useMemo(() => {
    if (!isObjectRecord(selectedItem)) {
      return [];
    }
    const assetIdsValue = selectedItem.assetIds;
    if (Array.isArray(assetIdsValue)) {
      return assetIdsValue.filter((id): id is string => typeof id === "string");
    }
    return [];
  }, [selectedItem]);

  const [selectedAssetIndex, setSelectedAssetIndex] = React.useState(0);

  React.useEffect(() => {
    if (
      isArrayForm &&
      selectedIndex >= formArray.length &&
      formArray.length > 0
    ) {
      setSelectedIndex(formArray.length - 1);
    }
  }, [isArrayForm, selectedIndex, formArray.length]);

  React.useEffect(() => {
    onConfirmationChange?.(confirmedParts);
  }, [confirmedParts, onConfirmationChange]);

  React.useEffect(() => {
    if (
      externalSelectedIndex !== undefined &&
      externalSelectedIndex !== selectedIndex
    ) {
      setSelectedIndex(externalSelectedIndex);
    }
  }, [externalSelectedIndex]);

  React.useEffect(() => {
    if (selectedItemAssetIds.length > 0) {
      setSelectedAssetIndex(0);
      setSelectedAssetId(selectedItemAssetIds[0] ?? null);
    } else {
      setSelectedAssetIndex(0);
      setSelectedAssetId(null);
    }
  }, [selectedIndex, selectedItemAssetIds]);

  const { autoSaving, queueEdit, flushPendingEdits } = useAutoSave<JSONValue>({
    onSave: handleSave,
    timeout: 1000,
  });

  React.useEffect(() => {
    const loadAsset = async (assetId: string) => {
      setAssetLoading(true);
      setAssetError(null);
      try {
        const response = await fetchApi(`api/assets?ids=${assetId}`, "GET");
        if (!response.ok) {
          setSelectedAsset(null);
          setAssetError("Failed to load asset");
          return;
        }
        const parsed = GetAssetsByIdsResponseSchema.safeParse(response.data);
        if (!parsed.success) {
          setSelectedAsset(null);
          setAssetError("Failed to load asset");
          return;
        }
        setSelectedAsset(parsed.data.assets[0] ?? null);
      } catch (_error) {
        setSelectedAsset(null);
        setAssetError("Failed to load asset");
      } finally {
        setAssetLoading(false);
      }
    };

    if (!selectedAssetId) {
      setSelectedAsset(null);
      setAssetError(null);
      return;
    }

    const debounceTimeout = setTimeout(() => {
      loadAsset(selectedAssetId).catch(() => {});
    }, 300);

    return () => clearTimeout(debounceTimeout);
  }, [selectedAssetId]);

  const saveConfirmation = React.useCallback(
    async (newConfirmedParts: Set<number>) => {
      if (!userReviewSchema || !userReviewForm) {
        return;
      }

      if (operationId) {
        const baseArgs = richActionData.actionArguments;
        const payload: Record<string, unknown> = {
          ...baseArgs,
          formJson: JSON.stringify(userReviewForm),
          formJsonSchema: JSON.stringify(userReviewSchema),
          confirmedIndices: Array.from(newConfirmedParts),
        };

        const response = await fetchApi(
          `agents/operations/${operationId}/input`,
          "PATCH",
          { body: { input: payload } },
        );

        if (!response.ok) {
          const responseData =
            typeof response.data === "object" && response.data !== null
              ? response.data
              : null;
          const errorMessage =
            responseData &&
            "error" in responseData &&
            typeof responseData.error === "string"
              ? responseData.error
              : "Failed to save form";
          setSaveError(errorMessage);
          return;
        }

        setSaveError(null);
        return;
      }

      if (!hasSdkContext || !sdkThreadId || !sdkMessageId) {
        return;
      }

      const success = await saveUserReviewOutput(
        sdkThreadId,
        sdkMessageId,
        userReviewForm,
        Array.from(newConfirmedParts),
      );
      if (success) {
        setSaveError(null);
      }
    },
    [
      operationId,
      hasSdkContext,
      sdkThreadId,
      sdkMessageId,
      saveUserReviewOutput,
      richActionData.actionArguments,
      userReviewSchema,
      userReviewForm,
    ],
  );

  const findNextUnconfirmedIndex = React.useCallback(
    (currentIndex: number, confirmedSet: Set<number>): number | null => {
      for (let i = currentIndex + 1; i < formArray.length; i++) {
        if (!confirmedSet.has(i)) {
          return i;
        }
      }
      for (let i = 0; i < currentIndex; i++) {
        if (!confirmedSet.has(i)) {
          return i;
        }
      }
      return null;
    },
    [formArray.length],
  );

  const handleConfirmToggle = React.useCallback(async () => {
    await flushPendingEdits();

    const newConfirmedParts = new Set(confirmedParts);
    const isCurrentlyConfirmed = newConfirmedParts.has(selectedIndex);

    if (isCurrentlyConfirmed) {
      newConfirmedParts.delete(selectedIndex);
    } else {
      newConfirmedParts.add(selectedIndex);

      const nextUnconfirmedIndex = findNextUnconfirmedIndex(
        selectedIndex,
        newConfirmedParts,
      );
      if (nextUnconfirmedIndex !== null) {
        setSelectedIndex(nextUnconfirmedIndex);
        onSelectedIndexChange?.(nextUnconfirmedIndex);
      }
    }
    setConfirmedParts(newConfirmedParts);
    saveConfirmation(newConfirmedParts).catch(() => {});
  }, [
    flushPendingEdits,
    confirmedParts,
    selectedIndex,
    saveConfirmation,
    findNextUnconfirmedIndex,
    onSelectedIndexChange,
  ]);

  const handleSelectIndex = React.useCallback(
    (index: number) => {
      setSelectedIndex(index);
      onSelectedIndexChange?.(index);
    },
    [onSelectedIndexChange],
  );

  const handleFormChange = React.useCallback(
    (data: JSONValue, shouldReloadSchema: boolean) => {
      if (shouldReloadSchema) {
        setLastSavedAt(null);
      }

      if (isApproving || isDenying || !formValidator || !userReviewSchema) {
        return;
      }

      const updatedForm = isArrayForm
        ? formArray.map((item, idx) => (idx === selectedIndex ? data : item))
        : data;

      setUserReviewForm(updatedForm);
      const isValid = formValidator(updatedForm);
      if (!isValid) {
        const message =
          formValidator.errors?.[0]?.message || "Form contains invalid data";
        setValidationError(message);
        return;
      }

      setValidationError(null);
      setSaveError(null);

      if (operationId || hasSdkContext) {
        queueEdit(updatedForm);
      }
    },
    [
      formValidator,
      isApproving,
      isDenying,
      queueEdit,
      userReviewSchema,
      isArrayForm,
      formArray,
      selectedIndex,
      operationId,
      hasSdkContext,
    ],
  );

  const getStatus = () => {
    if (!operationId && !hasSdkContext) {
      return null;
    }
    if (sdkResolveError) {
      return { intent: "error" as const, message: sdkResolveError };
    }
    if (schemaError) {
      return { intent: "error" as const, message: schemaError };
    }
    if (validationError) {
      return { intent: "error" as const, message: validationError };
    }
    if (saveError) {
      return { intent: "error" as const, message: saveError };
    }
    if (sdkSaveError) {
      return { intent: "error" as const, message: sdkSaveError };
    }
    if (autoSaving || sdkIsSaving) {
      return { intent: "saving" as const, message: "Saving..." };
    }
    if (lastSavedAt) {
      return {
        intent: "success" as const,
        message: `Saved ${formatDistanceToNow(lastSavedAt, { addSuffix: true })}`,
      };
    }
    if (currentOperationUpdatedAt) {
      return {
        intent: "success" as const,
        message: `Last updated ${formatDistanceToNow(
          new Date(currentOperationUpdatedAt),
          { addSuffix: true },
        )}`,
      };
    }
    return null;
  };

  const status = getStatus();

  const isOperationCompleted = currentOperationStatus === "COMPLETED";

  if (!userReviewParams || !userReviewSchema) {
    if (hasSdkContext && isLoadingSdkResolvedParams) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Loading form data</span>
          </div>
        </div>
      );
    }
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-muted-foreground">No form data available</p>
      </div>
    );
  }

  if (isArrayForm && formArray.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-muted-foreground">No items to review</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col md:flex-row h-full">
      {isArrayForm && (
        <div className="hidden md:flex">
          <IndexSelector
            items={formArray}
            selectedIndex={selectedIndex}
            onSelectIndex={handleSelectIndex}
            confirmedParts={confirmedParts}
          />
        </div>
      )}

      {selectedAssetId && (
        <div className="flex-1 md:border-r-2 border-b-2 md:border-b-0 border-border bg-muted/20 relative animate-in fade-in duration-300 flex flex-col min-h-[300px] md:min-h-0">
          <div className="flex items-center justify-center p-2 border-b-2 border-border bg-background/80 relative">
            <div className="flex items-center gap-1">
              {selectedItemAssetIds.length > 1 && (
                <Button
                  variant="outline"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => {
                    const newIndex = Math.max(0, selectedAssetIndex - 1);
                    setSelectedAssetIndex(newIndex);
                    setSelectedAssetId(selectedItemAssetIds[newIndex] ?? null);
                  }}
                  disabled={selectedAssetIndex === 0}
                >
                  <ChevronLeft className="h-3 w-3" />
                </Button>
              )}

              <span className="text-xs text-muted-foreground px-2">
                {assetLoading
                  ? "Loading..."
                  : selectedAsset?.fileName
                    ? selectedItemAssetIds.length > 1
                      ? `${selectedAsset.fileName} (${selectedAssetIndex + 1} of ${selectedItemAssetIds.length})`
                      : selectedAsset.fileName
                    : "Asset"}
              </span>

              {selectedItemAssetIds.length > 1 && (
                <Button
                  variant="outline"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => {
                    const newIndex = Math.min(
                      selectedItemAssetIds.length - 1,
                      selectedAssetIndex + 1,
                    );
                    setSelectedAssetIndex(newIndex);
                    setSelectedAssetId(selectedItemAssetIds[newIndex] ?? null);
                  }}
                  disabled={
                    selectedAssetIndex === selectedItemAssetIds.length - 1
                  }
                >
                  <ChevronRight className="h-3 w-3" />
                </Button>
              )}
            </div>

            <Button
              variant="ghost"
              size="icon"
              className="absolute right-2 h-6 w-6"
              onClick={() => setSelectedAssetId(null)}
              aria-label="Close asset viewer"
            >
              <X className="h-3 w-3" />
            </Button>
          </div>

          <div className="flex-1 overflow-hidden">
            {assetLoading ? (
              <div className="flex h-full items-center justify-center">
                <p className="text-sm text-muted-foreground">Loading asset…</p>
              </div>
            ) : assetError ? (
              <div className="flex h-full items-center justify-center">
                <p className="text-sm text-destructive">{assetError}</p>
              </div>
            ) : selectedAsset ? (
              <div className="h-full w-full">
                <AssetFileViewer asset={selectedAsset} />
              </div>
            ) : (
              <div className="flex h-full items-center justify-center">
                <p className="text-sm text-muted-foreground">
                  Preview unavailable
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      <ScrollArea
        className={cn(
          "p-2 bg-background",
          selectedAssetId
            ? "flex-1 md:min-w-[480px]"
            : "flex-1 md:min-w-[600px]",
        )}
      >
        <div className="space-y-4">
          {status && (
            <div className="px-4">
              <div
                className={cn(
                  "flex items-center gap-2 rounded-md border px-3 py-2 text-sm",
                  status.intent === "error"
                    ? "bg-red-50 text-red-700 border-red-200"
                    : status.intent === "saving"
                      ? "bg-blue-50 text-blue-700 border-blue-200"
                      : "bg-green-50 text-green-700 border-green-200",
                )}
              >
                {status.intent === "error" ? (
                  <AlertCircle className="h-4 w-4" />
                ) : status.intent === "saving" ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Check className="h-4 w-4" />
                )}
                <span>{status.message}</span>
              </div>
            </div>
          )}

          {itemSchema && (
            <>
              <div className="px-4">
                <JSONSchemaForm
                  schema={itemSchema}
                  formData={selectedItem || {}}
                  onChange={handleFormChange}
                  disabled={
                    isApproving || isDenying || (!operationId && !hasSdkContext)
                  }
                  showSubmitButton={false}
                  onClickAsset={(assetId) => {
                    const assetIndex = selectedItemAssetIds.indexOf(assetId);
                    if (assetIndex !== -1) {
                      setSelectedAssetIndex(assetIndex);
                    }
                    setSelectedAssetId(assetId);
                  }}
                />
              </div>

              {isArrayForm && (
                <div className="mt-6 pt-4 border-t px-4">
                  {confirmedParts.has(selectedIndex) ? (
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="lg"
                        className="flex-1 bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100 hover:text-emerald-800"
                        disabled
                      >
                        <Check className="mr-2 h-5 w-5" />
                        Confirmed
                      </Button>
                      <Button
                        variant="ghost"
                        size="lg"
                        onClick={handleConfirmToggle}
                        disabled={
                          isApproving || isDenying || isOperationCompleted
                        }
                      >
                        Unconfirm
                      </Button>
                    </div>
                  ) : (
                    <>
                      <Button
                        size="lg"
                        className="w-full"
                        onClick={handleConfirmToggle}
                        disabled={
                          isApproving ||
                          isDenying ||
                          !!validationError ||
                          !!schemaError
                        }
                      >
                        Confirm Part
                      </Button>
                      {(validationError || schemaError) && (
                        <p className="text-sm text-muted-foreground mt-2 text-center">
                          Cannot confirm: form has errors
                        </p>
                      )}
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};
